import type {
    ApiSpinTogetherDisplayConfigAny,
    ApiSpinTogetherDisplayConfigV2,
    ApiSpinTogetherFriendStateAny,
    ApiSpinTogetherPairSessionV2,
    ApiSpinTogetherRewardAny,
} from "@bridge-applications/pb-types";

export interface SpinTogetherEnergyBenefit {
    amount: string | null;
}

export interface SpinTogetherWinBoostBenefit {
    basisPoints: number | null;
    endsAt: string;
}

export interface SpinTogetherBenefitSummary {
    energy: SpinTogetherEnergyBenefit | null;
    winBoost: SpinTogetherWinBoostBenefit | null;
}

export interface SpinTogetherActiveBoostSummary {
    basisPoints: number;
    activeFriendCount: number;
    endsAt: string;
}

type SpinTogetherEnergyReward = Extract<
    ApiSpinTogetherRewardAny,
    { kind: "SESSION_START_ENERGY" }
>;

interface ActiveContributingSession {
    session: ApiSpinTogetherPairSessionV2;
    endsAt: string;
}

type EnabledWinBoostConfig = Extract<
    ApiSpinTogetherDisplayConfigV2["winBoost"],
    { enabled: true }
>;

function isDisplayConfigV2(
    config: ApiSpinTogetherDisplayConfigAny | null,
): config is ApiSpinTogetherDisplayConfigV2 {
    return config !== null && "policySchemaVersion" in config;
}

function isPairSessionV2(
    session: ApiSpinTogetherFriendStateAny["session"],
): session is ApiSpinTogetherPairSessionV2 {
    return session !== null && "policySchemaVersion" in session;
}

function policyCodeMatches(
    session: ApiSpinTogetherPairSessionV2,
    config: ApiSpinTogetherDisplayConfigV2,
): boolean {
    return session.policyCode === config.policyCode;
}

function policyVersionMatches(
    session: ApiSpinTogetherPairSessionV2,
    config: ApiSpinTogetherDisplayConfigV2,
): boolean {
    return (
        policyCodeMatches(session, config) &&
        session.policyVersion === config.policyVersion
    );
}

function activeContributingSession(
    friend: ApiSpinTogetherFriendStateAny,
    nowMs: number,
): ActiveContributingSession | null {
    const { session } = friend;
    if (
        !isPairSessionV2(session) ||
        !session.rewardEntitlements.winBoostEntitled ||
        !session.contributesToCurrentBoost ||
        session.boostEndsAt === null
    ) {
        return null;
    }
    const boostEndsAtMs = Date.parse(session.boostEndsAt);
    const sessionExpiresAtMs = Date.parse(session.expiresAt);
    return Number.isFinite(boostEndsAtMs) &&
        boostEndsAtMs > nowMs &&
        Number.isFinite(sessionExpiresAtMs) &&
        sessionExpiresAtMs > nowMs
        ? {
              session,
              endsAt:
                  sessionExpiresAtMs < boostEndsAtMs
                      ? session.expiresAt
                      : session.boostEndsAt,
          }
        : null;
}

function energyAmountForSession(
    session: ApiSpinTogetherPairSessionV2,
    config: ApiSpinTogetherDisplayConfigAny | null,
    recentRewards: readonly ApiSpinTogetherRewardAny[],
): string | null {
    const grantedReward = recentRewards.find(
        (reward): reward is SpinTogetherEnergyReward =>
            reward.kind === "SESSION_START_ENERGY" &&
            reward.sessionId === session.sessionId,
    );
    if (grantedReward) return grantedReward.energyAmount;
    if (
        isDisplayConfigV2(config) &&
        policyVersionMatches(session, config) &&
        config.sessionStartEnergy.enabled
    ) {
        return config.sessionStartEnergy.amountPerParticipant;
    }
    return null;
}

function tierBasisPoints(
    config: EnabledWinBoostConfig,
    activeSessionCount: number,
): number | null {
    const appliedSessionCount = Math.min(
        activeSessionCount,
        config.maxConcurrentRewardedSessionsPerUser,
    );
    let totalBasisPoints: number | null = null;
    for (const tier of config.tiers) {
        if (tier.minimumConcurrentSessions > appliedSessionCount) break;
        totalBasisPoints = tier.totalBasisPoints;
    }
    return totalBasisPoints;
}

export function spinTogetherActiveBoostSummary(
    config: ApiSpinTogetherDisplayConfigAny | null,
    friends: readonly ApiSpinTogetherFriendStateAny[],
    nowMs: number,
): SpinTogetherActiveBoostSummary | null {
    if (!isDisplayConfigV2(config) || !config.winBoost.enabled) {
        return null;
    }
    const activeSessions = friends
        .map((friend) => activeContributingSession(friend, nowMs))
        .filter(
            (activeSession): activeSession is ActiveContributingSession =>
                activeSession !== null &&
                policyCodeMatches(activeSession.session, config),
        );
    if (activeSessions.length === 0) return null;
    const totalBasisPoints = tierBasisPoints(
        config.winBoost,
        activeSessions.length,
    );
    if (totalBasisPoints === null) return null;

    const sessionsByExpiry = [...activeSessions].sort(
        (left, right) => Date.parse(left.endsAt) - Date.parse(right.endsAt),
    );
    let remainingSessionCount = sessionsByExpiry.length;
    let index = 0;
    while (index < sessionsByExpiry.length) {
        const expiringSession = sessionsByExpiry[index];
        if (!expiringSession) return null;
        const expiryMs = Date.parse(expiringSession.endsAt);
        let expiringSessionCount = 0;
        while (
            index < sessionsByExpiry.length &&
            Date.parse(sessionsByExpiry[index]?.endsAt ?? "") === expiryMs
        ) {
            expiringSessionCount += 1;
            index += 1;
        }
        remainingSessionCount -= expiringSessionCount;
        if (
            tierBasisPoints(config.winBoost, remainingSessionCount) !==
            totalBasisPoints
        ) {
            return {
                basisPoints: totalBasisPoints,
                activeFriendCount: activeSessions.length,
                endsAt: expiringSession.endsAt,
            };
        }
    }
    return null;
}

export function spinTogetherBenefitSummary(
    selectedFriend: ApiSpinTogetherFriendStateAny | null,
    config: ApiSpinTogetherDisplayConfigAny | null,
    friends: readonly ApiSpinTogetherFriendStateAny[],
    recentRewards: readonly ApiSpinTogetherRewardAny[],
    nowMs: number,
): SpinTogetherBenefitSummary | null {
    if (!selectedFriend || !isPairSessionV2(selectedFriend.session)) {
        return null;
    }
    const session = selectedFriend.session;

    const energy = session.rewardEntitlements.sessionStartEnergyEntitled
        ? {
              amount: energyAmountForSession(session, config, recentRewards),
          }
        : null;
    const activeSession = activeContributingSession(selectedFriend, nowMs);
    const activeBoost = activeSession
        ? isDisplayConfigV2(config) &&
          policyCodeMatches(activeSession.session, config)
            ? spinTogetherActiveBoostSummary(config, friends, nowMs)
            : null
        : null;
    const winBoost =
        activeSession === null
            ? null
            : {
                  basisPoints: activeBoost?.basisPoints ?? null,
                  endsAt:
                      activeBoost &&
                      Date.parse(activeBoost.endsAt) <
                          Date.parse(activeSession.endsAt)
                          ? activeBoost.endsAt
                          : activeSession.endsAt,
              };

    return energy || winBoost ? { energy, winBoost } : null;
}
